import CircularProgress from '$tablet-app/src/app/components/CircularProgress';
import { EmojiWithBackground } from '$tablet-app/src/app/components/EmojiWithBackground/EmojiWithBackground';
import Row from '$tablet-app/src/app/components/Row';
import Text from '$tablet-app/src/app/components/Text';
import { palette } from '$tablet-app/src/app/theme';
import { Goal } from '@akin-home/goals';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';

interface Props {
  /** The goal being rendered */
  goal: Goal;
  diameter?: number;
  width?: number;
  progress?: number;
}

const GoalCompletionCircle: FC<Props> = (props) => {
  const { goal, diameter = 170, width = 10, progress = 0 } = props;
  const { emoji, completions_per_period } = goal;
  const isComplete = progress === 100;

  const completionDisplay = Math.floor(
    completions_per_period - (progress / 100) * completions_per_period
  );

  return (
    <CircleWrapper height={diameter}>
      <Row
        position="absolute"
        width="100%"
        top={-15}
        justifyContent="center"
        zIndex={100}
      >
        <Count complete={isComplete}>
          {isComplete ? (
            <FontAwesomeIcon icon="check" color="white" />
          ) : (
            <Text weight="semibold">{completionDisplay}</Text>
          )}
        </Count>
      </Row>

      <CircularProgress
        radius={diameter / 2}
        rotation={0}
        strokeLinecap="butt"
        activeStrokeWidth={width}
        inActiveStrokeWidth={width}
        value={progress || 0}
        activeStrokeColor={
          isComplete ? palette.Emerald_green : palette.Primary_blue
        }
        inActiveStrokeColor="rgba(230, 232, 235, 1)"
      >
        <Emoji
          emoji={emoji || '🎯'}
          style={isComplete ? completedEmojiStyle : undefined}
        />
      </CircularProgress>

      {/* Add this back in once we figure out how to model it on a goal */}
      {completions_per_period > 1 ? (
        <DividerContainer>
          {[...Array(completions_per_period)].map((_, idx) => (
            <Divider
              key={idx}
              total={completions_per_period}
              number={idx + 1}
              height={width}
            />
          ))}
        </DividerContainer>
      ) : null}
    </CircleWrapper>
  );
};

const Count = styled(View)<{ complete?: boolean }>`
  border-radius: 10px;
  padding: 0 9px;
  height: 40px;
  min-width: 40px;
  justify-content: center;
  align-items: center;
  background-color: ${({ complete }) =>
    complete ? palette.Emerald_green : 'rgba(230, 232, 235, 1)'};
`;

const CircleWrapper = styled(View)<{ height: number }>`
  height: ${({ height }) => height}px;
  width: 100%;
  position: relative;
  align-items: center;
`;

const Divider = styled.View<{ total: number; number: number; height: number }>`
  height: ${({ height }) => height}px;
  width: 3px;
  background-color: white;
  position: absolute;
  transform: ${({ total, number }) => calculateDividerTransform(total, number)};
`;

const DividerContainer = styled.View`
  position: absolute;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const Emoji = styled(EmojiWithBackground)`
  border-radius: 50px;
  background-color: rgba(230, 232, 235, 1);
`;

const completedEmojiStyle = { backgroundColor: 'rgba(16, 185, 129, 0.1)' };

/**
 * Calculates the translation and rotation of a divider
 * based on how many sections there are and which divider count it is.
 */
const calculateDividerTransform = (total: number, number: number) => {
  const percentage = number / total;
  const angle = percentage * 360 - 90;

  const radians = (angle * Math.PI) / 180;
  const x = Math.cos(radians) * 80;
  const y = Math.sin(radians) * 80;
  return `rotate(${angle + 90}deg) translate(${x}px, ${y}px)`;
};

export default GoalCompletionCircle;
